import { memo, useState } from 'react';
import { AvatarIdentity } from '../core/avatarIdentity';
import { mediaUrls } from './media';
import { faceBackground } from './identityColors';
import { t } from '../i18n/i18n';
import { useAttendanceRing } from './features/attendance/attendance';

/**
 * A person's face: their photo when the server has one, a deterministic coloured-initials disc
 * otherwise (the same palette and hash as Apple, so a person is the same colour on every client).
 * The disc is drawn FIRST and the photo fades in over it only once decoded, so a row never
 * reflows and never flashes an empty circle (VISUAL-DESIGN §4/§5).
 */
export const Avatar = memo(function Avatar({
  userId,
  name,
  size = 32,
  version = 0,
  presence,
  imageUrl,
  colorKey,
  ring = false,
}: {
  userId: string;
  name: string;
  size?: number;
  version?: number;
  presence?: string | null;
  /** An integration's `override_icon_url` beats the account's own avatar. */
  imageUrl?: string | null;
  /** What the disc colour is keyed on, when not the account (an integration: `MessageRowLayout.identityKey`). */
  colorKey?: string;
  /** Draw the company-status ring (shared/API.md §43) around this person's face. */
  ring?: boolean;
}) {
  const attendance = useAttendanceRing(ring ? userId : null);
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const src = imageUrl || (version > 0 ? mediaUrls.avatar(userId, version) : null);
  return (
    <span className="avatar" data-ring={attendance.ring ?? undefined} title={attendance.label ?? undefined} style={{ width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.38)) }}>
      <span className="face" style={{ background: faceBackground(AvatarIdentity.color(colorKey || userId || name)) }}>
        {src && !failed ? (
          <img
            src={src}
            alt=""
            loading="lazy"
            decoding="async"
            style={{ opacity: loaded ? 1 : 0, transition: 'opacity var(--d-standard) var(--ease-out)' }}
            onLoad={() => setLoaded(true)}
            onError={() => setFailed(true)}
          />
        ) : null}
        {!loaded || failed || !src ? <span style={{ position: src && !failed ? 'absolute' : undefined }}>{AvatarIdentity.initials(name)}</span> : null}
      </span>
      {presence ? <span className={`presence ${presence}`} role="img" aria-label={presenceLabel(presence)} /> : null}
    </span>
  );
});

const PRESENCE_KEYS: Record<string, string> = { online: 'profile.status.online', away: 'profile.status.away', dnd: 'profile.status.dnd', offline: 'profile.status.offline' };

/** The dot's spoken name, in the UI language (it read the raw wire word «online» before). */
function presenceLabel(presence: string): string {
  const key = PRESENCE_KEYS[presence];
  return key ? t(key) : presence;
}
